import {
  type CreateStaffRequest,
  CreateStaffResponse,
  type CreateStoreRequest,
  CreateStoreResponse,
  type CreateStoreStaffRequest,
  CreateStoreStaffResponse,
  type DeleteStoreParameterRequest,
  DeleteStoreParameterResponse,
  type DeleteStoreStaffRequest,
  DeleteStoreStaffResponse,
  type GetStoreStaffsRequest,
  GetStoreStaffsResponse,
  type IssueBalanceRequest,
  IssueBalanceResponse,
  type UpdateStoreStaffRequest,
  UpdateStoreStaffResponse,
  type UpdateStoreImageRequest,
  UpdateStoreImageResponse,
  type UpdateStoreRequest,
  UpdateStoreResponse,
} from "schema/gen/es/chiikipoint/adminconsole/v2/service_pb";
import { StoreStaff } from "schema/gen/es/chiikipoint/model/v2/model_pb";

import { debugLog } from "../../debug-log";
import { nextPrefixedId, nextReadableId, simulateDelay } from "./utils";

const storeStaffsByStoreId = new Map<string, StoreStaff[]>([
  [
    "8f0d2d9e-c9c9-4b4a-9f9c-0c5c9c9c9c9c",
    [
      new StoreStaff({
        slug: "store-demo-001",
        storeId: "8f0d2d9e-c9c9-4b4a-9f9c-0c5c9c9c9c9c",
      }),
    ],
  ],
]);

const getStoreStaffState = (storeId: string): StoreStaff[] => {
  const current = storeStaffsByStoreId.get(storeId);
  if (current) {
    return current;
  }
  const next: StoreStaff[] = [];
  storeStaffsByStoreId.set(storeId, next);
  return next;
};

export const mockCreateStore = async (
  req: CreateStoreRequest,
): Promise<CreateStoreResponse> => {
  await simulateDelay();
  return new CreateStoreResponse({
    companyId: req.companyId,
    storeId: nextReadableId("created-store", 8),
  });
};

export const mockUpdateStore = async (
  req: UpdateStoreRequest,
): Promise<UpdateStoreResponse> => {
  await simulateDelay();
  return new UpdateStoreResponse({
    storeId: req.storeId,
  });
};

export const mockUpdateStoreImage = async (
  _req: UpdateStoreImageRequest,
): Promise<UpdateStoreImageResponse> => {
  await simulateDelay();
  return new UpdateStoreImageResponse();
};

export const mockCreateStaff = async (
  req: CreateStaffRequest,
): Promise<CreateStaffResponse> => {
  await simulateDelay();
  const password = nextPrefixedId("staff-password", "pass-", {
    pad: 6,
    radix: 36,
  });

  return new CreateStaffResponse({
    companyId: req.companyId,
    password,
    slug: req.slug,
  });
};

export const mockGetStoreStaffs = async (
  req: GetStoreStaffsRequest,
): Promise<GetStoreStaffsResponse> => {
  await simulateDelay();
  const storeId = req.query?.storeId;
  const staffs = storeId ? getStoreStaffState(storeId) : [];
  return new GetStoreStaffsResponse({
    staffs: staffs.map((staff) => new StoreStaff(staff)),
  });
};

export const mockCreateStoreStaff = async (
  req: CreateStoreStaffRequest,
): Promise<CreateStoreStaffResponse> => {
  await simulateDelay();
  const slug = req.slug || nextPrefixedId("store-staff-slug", "store-", {
    pad: 6,
    radix: 10,
  });
  const password = req.password || nextPrefixedId(
    "store-staff-password",
    "store-pass-",
    { pad: 6, radix: 36 },
  );
  const staffs = getStoreStaffState(req.storeId);
  const nextStaff = new StoreStaff({
    slug,
    storeId: req.storeId,
  });
  const existingIndex = staffs.findIndex((staff) => staff.slug === slug);
  if (existingIndex >= 0) {
    staffs.splice(existingIndex, 1, nextStaff);
  } else {
    staffs.push(nextStaff);
  }
  return new CreateStoreStaffResponse({
    storeId: req.storeId,
    password,
    slug,
  });
};

export const mockUpdateStoreStaff = async (
  req: UpdateStoreStaffRequest,
): Promise<UpdateStoreStaffResponse> => {
  await simulateDelay();
  for (const staffs of storeStaffsByStoreId.values()) {
    const index = staffs.findIndex((staff) => staff.slug === req.slug);
    if (index >= 0) {
      const current = staffs[index];
      staffs[index] = new StoreStaff({
        slug: req.newSlug || current.slug,
        storeId: current.storeId,
      });
      break;
    }
  }
  return new UpdateStoreStaffResponse();
};

export const mockDeleteStoreStaff = async (
  req: DeleteStoreStaffRequest,
): Promise<DeleteStoreStaffResponse> => {
  await simulateDelay();
  for (const [storeId, staffs] of storeStaffsByStoreId.entries()) {
    const nextStaffs = staffs.filter((staff) => staff.slug !== req.slug);
    if (nextStaffs.length !== staffs.length) {
      storeStaffsByStoreId.set(storeId, nextStaffs);
      break;
    }
  }
  return new DeleteStoreStaffResponse();
};

export const mockDeleteStoreParameter = async (
  _req: DeleteStoreParameterRequest,
): Promise<DeleteStoreParameterResponse> => {
  await simulateDelay();
  return new DeleteStoreParameterResponse();
};

export const mockIssueBalance = async (
  req: IssueBalanceRequest,
): Promise<IssueBalanceResponse> => {
  await simulateDelay();
  debugLog("mockIssueBalance", req.toJson());
  return new IssueBalanceResponse({
    transactionId: nextPrefixedId("issue-balance", "txn-", {
      pad: 6,
      radix: 36,
    }),
  });
};
